import React, {Component} from 'react';
import {saveAs} from 'file-saver';
import {formatBackendDateTime} from '../../../../../utils/DateUtils';
import PropTypes from 'prop-types';
import Linkify from 'linkifyjs/react';
import linkifyHtml from 'linkifyjs/html';
import ContactAvatar from '../../../../../components-new/general/ContactAvatar';
import moment from 'moment';
import {FileMapper} from '../../../../../utils/FileService';
import AudioPlayer from '../../../../../components-new/general/AudioPlayer';
import config from '../../../../../config';
import {convertFormBackendDate, groupReactionsHelper, MessageType} from '../../../../../utils/General';
import ContextMenu from "../../../../../components-new/ui/ContextMenu";
import Translate from "../../../../../components/Translate";
import {addCustomTemplate} from "../../../../../services/company/api";
import {addNotification as addNotificationAction} from "../../../../../services/notification/action";
import {connect} from "react-redux";
import {deleteMessage} from "../../../../../services/chat/api";
import Colors from "../../../../../utils/Colors";
import EditSquareIcon from "../../../../../assets/icons/EditSquareIcon";
import PaperClipIcon from "../../../../../assets/icons/PaperClipIcon";
import WarningIcon from "../../../../../assets/icons/WarningIcon";
import Tooltip from "../../../../../components-new/general/Tooltip";
import DownloadIcon from "../../../../../assets/icons/DownloadIcon";
import MarkdownRenderer from "../../../../../components-new/general/MarkdownRenderer";
import {sanitizeHtml} from "../../../../../utils/Html";
import CheckIcon from "../../../../../assets/icons/CheckIcon";
import DeleteIcon from "../../../../../assets/icons/DeleteIcon";
import ReplyIcon from "../../../../../assets/icons/ReplyIcon";
import FolderInputIcon from "../../../../../assets/icons/FolderInputIcon";
import CopyIcon from "../../../../../assets/icons/CopyIcon";
import CheckCheckIcon from "../../../../../assets/icons/CheckCheckIcon";
import FileIcon from "../../../../../assets/icons/FileIcon";
import LockKeyholeIcon from "../../../../../assets/icons/LockKeyholeIcon";
import {allowedReactions, defaultReaction} from "../../../../../utils/Emojis";
import MessageReaction from "./MessageReaction";
import {sendChatReactionRequested} from "../../../../../services/chat/action";

// linkify already defaults to _blank for urls; spelled out here so the intent
// survives a version bump, and to add rel — an opened tab must not get
// window.opener access to the dashboard
const LINKIFY_OPTIONS = {
    target: '_blank',
    attributes: {rel: 'noopener noreferrer'},
};

const botMessageHtml = (text) => ({__html: sanitizeHtml(linkifyHtml(text || '', LINKIFY_OPTIONS))});

class Message extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showContextMenu: false,
            contextMenuPosition: {x: 0, y: 0},
            contextMenuBounds: null,
            isDeleted: false,
            isEdited: false,
        };
    }

    handleContextMenu = (event) => {
        event.preventDefault();

        if (!this.hasContextMenuContent()) return;

        const container = document.querySelector('.messages-window');
        if (!container) return;

        const containerRect = container.getBoundingClientRect();

        const MENU_WIDTH = 260;

        let x = event.clientX;
        let y = event.clientY;

        if (x + MENU_WIDTH > containerRect.right) {
            x = containerRect.right - MENU_WIDTH - 8;
        }
        if (x < containerRect.left) {
            x = containerRect.left + 8;
        }

        this.setState({
            showContextMenu: true,
            contextMenuPosition: {x, y},
            contextMenuBounds: {
                top: containerRect.top,
                right: containerRect.right,
                bottom: containerRect.bottom,
                left: containerRect.left
            }
        });
    };

    canReact = () => {
        const {message, conversation, company, messageConfig} = this.props;
        const features = company && company.tariff ? company.tariff.features : [];

        return !!messageConfig.operatorCanReactToMessage
            && conversation.platform === config.platformsMap.webchat
            && (message.draftMessage === false || message.draftMessage === undefined)
            && features.includes(config.features.MESSAGE_REACTIONS);
    };

    hasContextMenuContent = () => {
        const {message, user, conversation, company, messageConfig} = this.props;
        const features = company && company.tariff ? company.tariff.features : [];
        const isMediaType = ['FILE', 'VIDEO', 'PHOTO', 'AUDIO'].includes(message.type);
        const isNotFromUser = !Message.isUserReceiver(message, user);
        const canReply = features.includes(config.features.MESSAGE_REPLY)
            && conversation.platform === config.platformsMap.webchat
            && (message.draftMessage === false || message.draftMessage === undefined)
            && (message.commentMessage === false || message.commentMessage === undefined)
            && message.text !== 'Start' && message.text !== 'end';
        const canEdit = !isMediaType && isNotFromUser
            && conversation.platform === config.platformsMap.webchat
            && message.chatWithOperator
            && features.includes(config.features.MESSAGE_EDIT);
        const canDelete = !!messageConfig.operatorCanDeleteMessage
            && isNotFromUser
            && conversation.platform === config.platformsMap.webchat
            && message.chatWithOperator
            && features.includes(config.features.MESSAGE_DELETE);

        return !isMediaType || this.canDownload() || canReply || canEdit || canDelete || this.canReact();
    };

    canDownload = () => {
        const {message} = this.props;
        return ['FILE', 'VIDEO', 'PHOTO', 'AUDIO'].includes(message.type)
            && !!(message.attachment && message.attachment.url);
    };

    downloadAttachment = async () => {
        if (!this.canDownload() || this.downloadingAttachment) return;
        this.setState({showContextMenu: false});
        this.downloadingAttachment = true;
        const {message, addNotification} = this.props;
        try {
            const response = await fetch(message.attachment.url);
            if (!response.ok) throw new Error('Attachment download failed');
            const blob = await response.blob();
            saveAs(blob, message.attachment.name || `attachment-${message.id}`);
        } catch (error) {
            addNotification('attachmentDownloadFailed', config.notificationLevels.error);
        } finally {
            this.downloadingAttachment = false;
        }
    };

    static isUserReceiver(message, user) {
        try {
            const {to, from} = message;
            if (user) {
                if (to.platformId === user.id) {
                    return true;
                }
            }
            return (from.platform !== config.platformsMap.company && !from.bot);
        } catch (e) {
            return false;
        }
    }

    static getMessageContent(message, openImageViewer, openVideoViewer, isDeleted = false, isNotFromUser) {
        if (message.isFillProfileInputsMessage) {
            return (
                <div style={{
                    width: 'fit-content',
                    textAlign: 'center',
                    margin: '20px auto',
                    padding: '2px 10px',
                    borderRadius: '999px',
                    backgroundColor: 'var(--ds-bg-quaternary)',
                    color: 'var(--ds-text-secondary)',
                    fontSize: '12px'
                }}>
                    <Translate text="profileInfoInputsRequested"/>
                </div>
            );
        }

        if (isDeleted) {
            /*
             * The colour is the bubble's own. It was a hard-coded white, which
             * only ever worked on the two solid bubbles: on an internal comment,
             * which is amber on pale amber, it was white on cream, and on a photo
             * or a video - whose bubble is transparent, because the picture was
             * the bubble - it was white on the chat's own white ground. In both
             * cases the notice was there and could not be read.
             */
            return (
                <p className="message-deleted">
                    <Translate text="messageDeleted"/>
                </p>
            );
        }

        if (message.endMessage) {
            const endedBy = message.endBy === 'user' ? (
                <Translate text="chatEndedByUser"/>
            ) : (
                <React.Fragment>
                    <Translate text="chatEndedByOperator"/> ({message.endBy})
                </React.Fragment>
            );

            return (
                <div style={{
                    width: 'fit-content',
                    textAlign: 'center',
                    margin: '20px auto',
                    padding: '2px 10px',
                    borderRadius: '999px',
                    backgroundColor: 'var(--ds-bg-quaternary)',
                    color: 'var(--ds-text-secondary)',
                    fontSize: '12px'
                }}>
                    {endedBy} (
                    {formatBackendDateTime(message.date, 'en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                    })}
                    )

                </div>
            );
        }

        if (message.forwardMessage) {
            const forwardedBy = message.forwardedBy
            const forwardedTo = message.forwardedTo

            return (
                <div style={{
                    width: 'fit-content',
                    textAlign: 'center',
                    margin: '20px auto',
                    padding: '2px 10px',
                    borderRadius: '999px',
                    backgroundColor: 'var(--ds-bg-quaternary)',
                    color: 'var(--ds-text-secondary)',
                    fontSize: '12px'
                }}>
                    <Translate text={'chatForwarded'}/> ({forwardedBy} → {forwardedTo})

                    (
                    {formatBackendDateTime(message.date, 'en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                    })}
                    )
                </div>
            );
        }

        const messageType = message.type;
        let content;

        switch (messageType) {
            case MessageType.aiText:
                content = (
                    <Linkify tagName="span" options={LINKIFY_OPTIONS}>
                        {message.draftMessage && !message.commentMessage ? ( // draft message
                            <span className="message-draft">
                                <span className="typing-dots message-draft__dots">
                                    <span/><span/><span/>
                                </span>
                                <span className="message-draft__text">{message.text}</span>
                            </span>
                        ) : message.commentMessage && !message.notSent ? ( // comment message
                            <span className="animate-slide-in" style={{whiteSpace: 'pre-wrap'}}>
                                <MarkdownRenderer content={message.text}/>
                            </span>
                        ) : (
                            <span className="animate-fade-in"
                                  style={{whiteSpace: 'pre-wrap'}}>
                                <MarkdownRenderer content={message.text}/>
                            </span>
                        )}
                    </Linkify>
                );
                break;
            case MessageType.quickReply:
            case MessageType.text:
                content = (
                    <Linkify tagName="span" options={LINKIFY_OPTIONS}>
                        {message.draftMessage && !message.commentMessage ? ( // draft message
                            <span className="message-draft">
                                <span className="typing-dots message-draft__dots">
                                    <span/><span/><span/>
                                </span>
                                <span className="message-draft__text">{message.text}</span>
                            </span>
                        ) : message.commentMessage && !message.notSent ? ( // comment message
                            <span className="animate-slide-in" style={{whiteSpace: 'pre-wrap'}}>
                                {message.text}
                            </span>
                        ) : message.from && message.from.bot ? ( // bot message
                            <span className="animate-fade-in"
                                  style={{whiteSpace: 'pre-wrap'}}
                                  dangerouslySetInnerHTML={botMessageHtml(message.text)}
                            />
                        ) : (
                            <span className="animate-fade-in"
                                  style={{whiteSpace: 'pre-wrap'}}>
                                {message.text}
                            </span>
                        )}
                    </Linkify>
                );
                break;
            case FileMapper.image:
                content = (
                    <button
                        type="button"
                        className="message-visual-media-button"
                        onClick={() => openImageViewer(message.attachment)}
                    >
                        {message.attachment && message.attachment.url && (
                            <img
                                className="message-visual-media"
                                draggable={false}
                                onDragStart={event => event.preventDefault()}
                                src={message.attachment.url}
                                style={{height: '200px', width: 'auto', maxWidth: '100%'}}
                            />
                        )}
                    </button>
                );
                break;
            case FileMapper.video:
                content = (
                    <video className="message-visual-media"
                           onClick={() => openVideoViewer(message.attachment)}
                           style={{width: '200px', height: '200px', cursor: 'pointer'}}
                           controls>
                        {message.attachment && message.attachment.url && (
                            <source src={message.attachment.url}/>
                        )}
                    </video>
                );
                break;
            case FileMapper.audio:
                content = message.attachment && message.attachment.url
                    ? <AudioPlayer src={message.attachment.url}
                                   downloadName={message.attachment.name || 'voice-message'}/>
                    : null;
                break;
            case FileMapper.application:
                content = (
                    <a
                        className="message-file"
                        href={message.attachment.url}
                        target="_blank" // for other platform files (mb video but type is file)
                        rel="noopener noreferrer"
                    >
                        <span className="message-file__icon">
                            <FileIcon width={20} height={20} stroke="currentColor"/>
                        </span>
                        <Tooltip text={message.attachment.name} needTranslate={false}
                                 style={{minWidth: 0, flex: 1}}>
                            <span className="message-file__name">{message.attachment.name}</span>
                        </Tooltip>
                        <span className="message-file__download">
                            <DownloadIcon width={18} height={18}
                                          stroke="currentColor"/>
                        </span>
                    </a>
                )
                break;
            default:
                content = <p style={{color: 'var(--ds-text-on-solid)'}}>Message</p>;
        }

        return (
            <div>
                {content}
            </div>
        );
    }

    handleAction = (action) => {
        const {message, addNotification, updateTemplates, setReplyTo} = this.props;
        if (action === 'copy') {
            navigator.clipboard.writeText(message.text)
                .then(() => {
                    addNotification('copied', config.notificationLevels.info);
                })
                .catch(() => {
                    addNotification('somethingWentWrong', config.notificationLevels.error);
                })
        } else if (action === 'addTemplate') {
            addCustomTemplate(message.text)
                .then((r) => {
                    updateTemplates(r)
                    addNotification('added', config.notificationLevels.success);
                })
                .catch(() => {
                    addNotification('somethingWentWrong', config.notificationLevels.error);
                })
        } else if (action === 'delete') {
            deleteMessage(message.id)
                .then(() => {
                    this.setState({isDeleted: true});
                    addNotification('messageDeleted', config.notificationLevels.info);
                })
                .catch((error) => {
                    if (error.response && error.response.status === 423) {
                        addNotification('actionBlockedByAdmin', config.notificationLevels.warning);
                    } else if (error.response && error.response.status === 409) {
                        addNotification('notYourMessage', config.notificationLevels.warning);
                    } else {
                        addNotification('somethingWentWrong', config.notificationLevels.error);
                    }
                })
        } else if (action === 'edit') {
            this.props.setEditingMessage(message);
        } else if (action === 'reply') {
            setReplyTo(message);
            this.props.childRef.current.focusTextareaToEnd(); // MessageInput ref
        }

        this.setState({showContextMenu: false});
    };

    renderContextMenu() {
        const {showContextMenu, contextMenuPosition, contextMenuBounds} = this.state;
        const {message, user, conversation, company, messageConfig} = this.props;

        let features = [];
        if (company && company.tariff) {
            features = company.tariff.features
        }

        if (!showContextMenu) return null;

        const isMediaType = ['FILE', 'VIDEO', 'PHOTO', 'AUDIO'].includes(message.type);
        const isNotFromUser = !Message.isUserReceiver(message, user);

        const items = [];

        if (this.canDownload()) {
            items.push({
                key: 'download',
                icon: <DownloadIcon width={18} height={18} stroke="currentColor"/>,
                label: <Translate text="download"/>,
                onClick: this.downloadAttachment,
            });
        }

        if (!isMediaType) {
            items.push({
                key: 'copy',
                icon: <CopyIcon strokeWidth={1.5} width={18} height={18} style={{color: Colors.blueDark}}
                                stroke="currentColor"/>,
                label: <Translate text="copy"/>,
                onClick: () => this.handleAction('copy'),
            });

            if (isNotFromUser && features.includes(config.features.TEMPLATES)) {
                items.push({
                    key: 'addTemplate',
                    icon: <FolderInputIcon strokeWidth={1.5} width={18} height={18} style={{color: Colors.blueDark}}
                                           stroke="currentColor"/>,
                    label: <Translate text="addTemplate"/>,
                    onClick: () => this.handleAction('addTemplate'),
                });
            }
        }

        if (features.includes(config.features.MESSAGE_REPLY)
            && conversation.platform === config.platformsMap.webchat
            && (message.draftMessage === false || message.draftMessage === undefined)
            && (message.commentMessage === false || message.commentMessage === undefined)
            && (message.text !== 'Start' && message.text !== 'end')
        ) {
            items.push({
                key: 'reply',
                icon: <ReplyIcon strokeWidth={1.5} width={18} height={18} style={{color: Colors.blueDark}}
                                 stroke="currentColor"/>,
                label: <Translate text="reply"/>,
                onClick: () => this.handleAction('reply'),
            });
        }

        if (!isMediaType
            && isNotFromUser
            && messageConfig.operatorCanEditMessage
            && conversation.platform === config.platformsMap.webchat
            && message.chatWithOperator
            && features.includes(config.features.MESSAGE_EDIT)
        ) {
            items.push({
                key: 'edit',
                icon: <EditSquareIcon strokeWidth={1.5} width={18} height={18} style={{color: Colors.blueDark}}
                                      stroke="currentColor"/>,
                label: <Translate text="edit"/>,
                onClick: () => this.handleAction('edit'),
            });
        }

        if (isNotFromUser
            && messageConfig.operatorCanDeleteMessage
            && conversation.platform === config.platformsMap.webchat
            && message.chatWithOperator
            && features.includes(config.features.MESSAGE_DELETE)
        ) {
            items.push({
                key: 'delete',
                danger: true,
                icon: <DeleteIcon strokeWidth={1.5} width={18} height={18} style={{color: Colors.blueDark}}
                                  stroke="currentColor"/>,
                label: <Translate text="delete"/>,
                onClick: () => this.handleAction('delete'),
            });
        }

        let emojiContainer = null
        if (this.canReact()) {
            emojiContainer = (
                <div
                    style={{display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'center'}}
                >
                    {allowedReactions.map((reaction, index) =>
                        <div
                            key={reaction}
                            className="emoji-item"
                            style={{
                                fontSize: '16px',
                                height: '26px',
                                width: '26px'
                            }}
                            onClick={() => {
                                this.props.sendChatReaction(message, reaction)
                                this.setState({showContextMenu: false});
                            }}
                        >
                            {reaction}
                        </div>
                    )}
                </div>
            )
        }

        if (items.length === 0 && !emojiContainer) return null;

        return (
            <ContextMenu
                position={contextMenuPosition}
                bounds={contextMenuBounds}
                items={items}
                onClose={() => this.setState({showContextMenu: false})}
                topBarContent={emojiContainer}
            />
        );
    }

    renderReplyPreview(parentMessage) {
        if (!parentMessage) return null;

        const isMedia = ['FILE', 'VIDEO', 'PHOTO', 'AUDIO'].includes(parentMessage.type);

        const handleClick = () => {
            const element = document.getElementById(`message-${parentMessage.id}`);
            if (!element) return;

            element.scrollIntoView({behavior: 'smooth', block: 'center'});

            const el = document.getElementById(`message-3-div-${parentMessage.id}`);
            if (!el) return;

            if (el._highlightTimeout) {
                clearTimeout(el._highlightTimeout);
            }
            el.classList.remove('highlight');
            setTimeout(() => {
                el.classList.add('highlight');
            }, 100)
            el._highlightTimeout = setTimeout(() => {
                el.classList.remove('highlight');
                el._highlightTimeout = null;
            }, 2000);
        };

        return (
            <button type="button" className="reply-quote" onClick={handleClick}>
                <span className="reply-quote__text">
                    {isMedia ? (
                        <span className="reply-quote__media">
                            <PaperClipIcon strokeWidth={1.5} width={13} height={13} stroke="currentColor"/>
                            <Translate text="attachment"/>
                        </span>
                    ) : (
                        parentMessage.text || ''
                    )}
                </span>
            </button>
        );

    }

    render() {
        const {
            message,
            openImageViewer,
            openVideoViewer,
            displayTimeLine,
            user,
            highlight,
            lastSeenMessageId,
            conversation,
            readOnly,
            sendChatReaction,
            company,
            openOperatorProfile,
            isNewMessage,
            isMessageSent,
        } = this.props;
        const {isDeleted} = this.state;

        let features = [];
        if (company && company.tariff) {
            features = company.tariff.features
        }


        let contact;
        if (message.fromSupervisor && message.supervisor) {
            contact = message.supervisor
        } else {
            contact = message.from
        }

        const parentMessage = message.parent

        let type = Message.isUserReceiver(message, user) ? 'received' : 'sent';
        if (conversation.platform === config.platformsMap.company) {
            type = !Message.isUserReceiver(message, user) ? 'received' : 'sent';
        }

        const messageTime = convertFormBackendDate(message.date);
        const hasMessageTime = !Number.isNaN(messageTime.getTime());

        if (message.id === lastSeenMessageId) {
            message.seenByUser = true
        }

        let isNotFromUser = !Message.isUserReceiver(message, user);
        if (conversation.platform === config.platformsMap.company) {
            isNotFromUser = !isNotFromUser;
        }

        const canOpenOperatorProfile = contact
            && contact.id
            && contact.platform === config.platformsMap.company
            && !contact.bot
            && typeof openOperatorProfile === 'function';

        const isSpecialMessage = message.endMessage || message.forwardMessage || message.isFillProfileInputsMessage;
        const isVisualMedia = message.type === FileMapper.image || message.type === FileMapper.video;
        const isAttachment = isVisualMedia || message.type === FileMapper.audio || message.type === FileMapper.application;
        const isCommentMessage = message.commentMessage && !message.notSent;

        return (
            <div>
                {displayTimeLine && hasMessageTime && (
                    <div className="timeline-wrapper">
                        <div className="timeline-info-parent">
                            <div className="timeline-info">
                                {moment(messageTime).format('MMMM D, YYYY')}
                            </div>
                        </div>
                    </div>
                )}

                {isSpecialMessage ? (
                    <div style={{marginBottom: '20px'}}>
                        {Message.getMessageContent(message, openImageViewer, openVideoViewer, isDeleted, isNotFromUser)}
                    </div>
                ) : (
                    <div
                        /* live: said while the operator was watching, and the only
                           kind that is animated - a chat being opened draws its
                           history at rest */
                        className={`message ${type}` + (isNewMessage || isMessageSent ? ' message--live' : '')}
                        key={message.id}
                        id={`message-${message.id}`}
                    >
                        <div
                            className="avatar-wrapper"
                            style={canOpenOperatorProfile ? {cursor: 'pointer'} : undefined}
                            onClick={canOpenOperatorProfile ? (event) => {
                                event.stopPropagation();
                                openOperatorProfile(contact);
                            } : undefined}
                        >
                            <ContactAvatar contact={contact} variant="chat"/>
                        </div>
                        <div>
                            <div
                                id={`message-3-div-${message.id}`}
                                onContextMenu={this.handleContextMenu}
                                className={`message-content ${isAttachment ? 'message-content--attachment' : ''} ${isVisualMedia ? 'message-content--visual-media' : ''}
                                ${isCommentMessage ? 'message-content--comment' : ''}
                                ${highlight ? 'highlight' : ''} 
                                ${this.state.isDeleted ? 'deleted' : ''} 
                                ${message.fromSupervisor ? 'supervisor-message' : ''}`
                                }
                                style={{marginBottom: '20px', transition: 'all 0.3s'}}
                            >
                                <div>
                                    {isCommentMessage && (
                                        <div className="message-comment-label">
                                            <LockKeyholeIcon width={14} height={14} stroke="currentColor"/>
                                            <Translate text="commentMessage"/>
                                        </div>
                                    )}
                                    {parentMessage && this.renderReplyPreview(parentMessage)}


                                    {Message.getMessageContent(message, openImageViewer, openVideoViewer, isDeleted, isNotFromUser)}

                                    {(message.operatorReaction || message.userReaction) && features.includes(config.features.MESSAGE_REACTIONS) &&
                                        <div className={'message-reactions-container'}>
                                            {groupReactionsHelper(message).map(r =>
                                                <MessageReaction
                                                    key={r.emoji}
                                                    emoji={r.emoji}
                                                    hasOwn={r.hasOwn}
                                                    count={r.count}
                                                    readonly={readOnly || !this.props.messageConfig.operatorCanReactToMessage}
                                                    onClick={() => sendChatReaction(message, r.emoji)}
                                                />
                                            )}
                                        </div>
                                    }
                                </div>

                                {
                                    !readOnly && this.props.messageConfig.operatorCanReactToMessage &&
                                    features.includes(config.features.MESSAGE_REACTIONS) &&
                                    (message.draftMessage === false || message.draftMessage === undefined) &&
                                    (conversation.platform === config.platformsMap.webchat)
                                    &&
                                    <div style={{position: 'relative', width: '100%', height: '2px'}}>
                                        <div
                                            onClick={() => sendChatReaction(message, defaultReaction)}
                                            className={"default-emoji emoji-item " + (message.operatorReaction ? "has-operator-reaction" : "")}
                                        >
                                            {defaultReaction}
                                        </div>
                                    </div>
                                }
                            </div>
                            <div>
                                <div className="message-time"
                                     style={{display: 'flex', alignItems: 'center', gap: '8px'}}>

                                    <div className="message-indicator-div">
                                        {hasMessageTime ? moment(messageTime).format('HH:mm') : '—'}
                                    </div>

                                    {(message.seenByUser && isNotFromUser) && (
                                        <div className="message-indicator-div message-tick message-tick--seen">
                                            <CheckCheckIcon style={{color: 'var(--ds-utility-brand-500)'}}
                                                            stroke="currentColor" width={15} height={15}/>
                                        </div>
                                    )}

                                    {(!message.seenByUser && isNotFromUser) && (
                                        <div className="message-indicator-div">
                                            <CheckIcon width={15} height={15}/>
                                        </div>
                                    )}

                                    {message.edited && (
                                        <Tooltip inRightSide={type !== 'sent'} inLeftSide={type === 'sent'}
                                                 height={14} text={'messageEdited'}>
                                            <div className="message-indicator-div">
                                                <EditSquareIcon style={{color: Colors.blueDark}} stroke="currentColor"
                                                                width={13} height={13}/>
                                            </div>
                                        </Tooltip>
                                    )}

                                    {message.notSent && (
                                        <div className="message-indicator-div">
                                            <Tooltip inRightSide={type !== 'sent'} inLeftSide={type === 'sent'}
                                                     background={Colors.red} height={14} text={'messageIsNotSent'}>
                                                <WarningIcon stroke="var(--ds-text-error-primary)" size={13}/>
                                            </Tooltip>
                                        </div>
                                    )}

                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {!readOnly && this.renderContextMenu()}

            </div>
        );
    }
}

const mapDispatchToProps = {
    addNotification: addNotificationAction,
    sendChatReaction: sendChatReactionRequested
};

Message.propTypes = {
    message: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    openImageViewer: PropTypes.func.isRequired,
    openVideoViewer: PropTypes.func.isRequired,
    displayTimeLine: PropTypes.bool.isRequired,
    sendChatReaction: PropTypes.func,
    messageConfig: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
    lastSeenMessageId: state.chat.lastSeenMessageId,
});

export default connect(mapStateToProps, mapDispatchToProps)(Message);



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/chat/chat-components/Message.jsx